import { Check, ChevronRight, Send, Undo2, X } from 'lucide-react';
import { useState } from 'react';
import { ApiError } from '../../lib/api';
import { useListState, usePage, type ListSpec } from '../../lib/list';
import { href } from '../../lib/router';
import { DataView } from '../../ui/DataView';
import { useToast } from '../../ui/Toasts';
import { usePendingRelease, useRelease, useReleaseAction, useStudioAccess, type Release } from './api';
import { when } from './StudioPage';

const STATUS: Record<Release['status'], { label: string; tone: string }> = {
  pending: { label: 'Waiting for approval', tone: 'warn-pill' },
  approved: { label: 'Published', tone: 'ok' },
  rejected: { label: 'Rejected', tone: 'bad' },
  withdrawn: { label: 'Withdrawn', tone: 'neutral' },
};

const SPEC: ListSpec = { id: 'releases', sort: 'created', view: 'cards', filters: ['status'] };

/**
 * Releases (maker-checker, decision 0004): an editor submits every current draft with a note; a second
 * person, never the author, approves it into a new version or rejects it with a reason.
 */
export function ReleasesPage() {
  const access = useStudioAccess();
  const list = useListState(SPEC);
  const releases = usePage<Release>('/config/releases', list.state);
  const pending = usePendingRelease().data;
  const act = useReleaseAction();
  const toast = useToast();
  const [note, setNote] = useState('');

  const run = async (a: Parameters<typeof act.mutateAsync>[0], done: string) => {
    try {
      await act.mutateAsync(a);
      toast(done, 'ok');
      return true;
    } catch (e) {
      toast(e instanceof ApiError && e.issues.length ? e.issues.map((i) => i.message).join('; ') : e instanceof Error ? e.message : 'Could not do that', 'bad'); // prettier-ignore
      return false;
    }
  };

  return (
    <>
      <div className="crumbs">
        <a href={href.studio()}>Studio</a>
        <ChevronRight size={14} />
        Releases
      </div>
      <div className="page-head">
        <h1>Releases</h1>
        <p>Every saved draft goes out together. A second person approves before anything reaches assessments.</p>
      </div>

      {access.edits && !pending && (
        <form
          className="card pad mb"
          onSubmit={async (e) => {
            e.preventDefault();
            if (await run({ type: 'submit', note }, 'Submitted for approval')) setNote('');
          }}
        >
          <div className="field">
            <label htmlFor="r-note">Submit the current drafts for approval</label>
            <textarea id="r-note" className="input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What changed and why, for the approver" />
          </div>
          <div className="row gap mt">
            <button type="submit" className="btn btn-primary" disabled={!note.trim() || act.isPending}>
              <Send size={15} /> Submit release
            </button>
            <span className="muted small">Drafts are checked as a whole first; problems block the release.</span>
          </div>
        </form>
      )}

      <DataView
        list={list}
        query={releases}
        noun="releases"
        rowKey={(r) => r.id}
        columns={[
          { key: 'created', label: 'Submitted', sort: 'created', render: (r) => <span className="muted">{when(r.submitted_at)}</span> },
          { key: 'release', label: 'Release', render: (r) => <><b>{r.kind === 'rollback' ? 'Rollback' : 'Release'}</b>{r.version_number !== null && <span className="muted"> · version {r.version_number}</span>}</> }, // prettier-ignore
          { key: 'note', label: 'Note', render: (r) => r.note },
          { key: 'by', label: 'By', wide: true, render: (r) => <span className="muted">{r.submitted_by_name ?? '–'}</span> },
          { key: 'status', label: 'Status', render: (r) => <span className={`pill ${STATUS[r.status].tone}`}>{STATUS[r.status].label}</span> },
        ]}
        wideCards
        card={(r) => <ReleaseCard r={r} mine={r.submitted_by === access.userId} approves={access.approves} edits={access.edits} busy={act.isPending} run={run} />}
        filters={[{ key: 'status', label: 'Statuses', options: (Object.keys(STATUS) as Release['status'][]).map((st) => [st, STATUS[st].label] as const) }]}
        sorts={[{ value: 'created', label: 'Submitted', dir: 'desc' }]}
        searchLabel="Search release notes"
        empty={<><Send size={28} /><strong>No releases yet</strong></>}
      />
    </>
  ); // prettier-ignore
}

function ReleaseCard({
  r,
  mine,
  approves,
  edits,
  busy,
  run,
}: {
  r: Release;
  mine: boolean;
  approves: boolean;
  edits: boolean;
  busy: boolean;
  run: (
    a: Parameters<ReturnType<typeof useReleaseAction>['mutateAsync']>[0],
    done: string,
  ) => Promise<boolean>;
}) {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  // What a pending release changes is shown at once; for decided ones, on request.
  const [open, setOpen] = useState(r.status === 'pending');
  const detail = useRelease(open ? r.id : null);
  const s = STATUS[r.status];
  return (
    <section className="card pad">
      <div className="row-between">
        <div>
          <b>{r.kind === 'rollback' ? 'Rollback' : 'Release'}</b>
          {r.version_number !== null && <span className="muted"> · version {r.version_number}</span>}
        </div>
        <span className={`pill ${s.tone}`}>{s.label}</span>
      </div>
      <p className="mt-sm">{r.note}</p>
      <p className="muted small">
        {r.submitted_by_name ?? 'Someone'} · {when(r.submitted_at)} · {r.items.length} items in all
        {r.decided_at && ` · ${r.status} by ${r.decided_by_name ?? 'someone'} on ${when(r.decided_at)}`}
      </p>
      {r.decision_note && <p className="small">“{r.decision_note}”</p>}
      {open ? (
        detail.isPending ? (
          <p className="muted small">Loading the changes…</p>
        ) : detail.isError ? (
          <p className="muted small">{detail.error.message}</p>
        ) : (
          <ul className="diff-list">
            {detail.data.diff.map((d) => (
              <li key={d.item_id}>
                <span className={`pill ${d.change === 'removed' ? 'bad' : d.change === 'added' ? 'ok' : 'neutral'}`}>{d.change}</span>
                <span>{d.kind}</span>
                <span className="mono small">{d.key}</span>
                <span className="muted small">
                  {d.from_revision ? `r${d.from_revision}` : ''}
                  {d.from_revision && d.to_revision ? ' → ' : ''}
                  {d.to_revision ? `r${d.to_revision}` : ''}
                </span>
              </li>
            ))}
          </ul>
        )
      ) : (
        <button type="button" className="linklike small" onClick={() => setOpen(true)}>
          Show what changed
        </button>
      )}
      {r.validation.length > 0 && (
        <ul className="problem-list">
          {r.validation.slice(0, 10).map((v, i) => (
            <li key={i}>
              <b>{v.kind} {v.key}:</b> {v.message}
            </li>
          ))}
        </ul>
      )}
      {r.status === 'pending' && (
        <div className="row gap mt">
          {approves && !mine && !rejecting && (
            <>
              <button type="button" className="btn btn-primary" disabled={busy} onClick={() => run({ type: 'approve', id: r.id }, 'Approved and published')}>
                <Check size={15} /> Approve and publish
              </button>
              <button type="button" className="btn" disabled={busy} onClick={() => setRejecting(true)}>
                <X size={15} /> Reject
              </button>
            </>
          )}
          {approves && mine && <span className="muted small">You submitted this, so someone else approves it.</span>}
          {edits && mine && (
            <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => run({ type: 'withdraw', id: r.id }, 'Withdrawn')}>
              <Undo2 size={15} /> Withdraw
            </button>
          )}
          {rejecting && (
            <form
              className="input-row grow"
              onSubmit={async (e) => {
                e.preventDefault();
                if (await run({ type: 'reject', id: r.id, note: reason }, 'Rejected')) setRejecting(false);
              }}
            >
              <input className="input" autoFocus aria-label="Why it is rejected" placeholder="Why, so the author can fix it" value={reason} onChange={(e) => setReason(e.target.value)} />
              <button type="submit" className="btn" disabled={!reason.trim() || busy}>
                Reject
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => setRejecting(false)}>
                Cancel
              </button>
            </form>
          )}
        </div>
      )}
    </section>
  ); // prettier-ignore
}
